"use client";

import { type ColumnDef, type Table } from "@tanstack/react-table";

import { CopyableContent } from "@/src/components/copyable-content";
import { DataTable } from "@/src/components/tables/data-table";
import { DataTableColumnHeader } from "@/src/components/tables/data-table-column-header";
import { type Diagnosis } from "@/src/models/documents/referral";
import { formatICD10CodeForDisplay } from "@/src/models/icd10";

interface DiagnosisListTableProps {
  diagnoses: Diagnosis[];
  fallbackDiagnosis?: {
    diagnosisCode: string | null;
    diagnosisText: string | null;
  } | null;
}

// Empty toolbar component for embedded tables without filters
function EmptyToolbar({ table: _table }: { table: Table<Diagnosis> }) {
  return null;
}

const CellWrapper = ({ children }: { children: React.ReactNode }) => {
  return <div className="w-full p-2 text-sm">{children}</div>;
};

const diagnosisCodeColumn: ColumnDef<Diagnosis> = {
  id: "diagnosisCode",
  accessorKey: "diagnosisCode",
  header: ({ column }) => <DataTableColumnHeader column={column} title="Code" className="ml-2" />,
  cell: ({ row }) => {
    const diagnosisCode = row.getValue("diagnosisCode") as string | null;

    return (
      <CellWrapper>
        <CopyableContent text={formatICD10CodeForDisplay(diagnosisCode) || "N/A"} />
      </CellWrapper>
    );
  },
  enableColumnFilter: false,
};

const diagnosisTextColumn: ColumnDef<Diagnosis> = {
  id: "diagnosisText",
  accessorKey: "diagnosisText",
  header: ({ column }) => <DataTableColumnHeader column={column} title="Text" />,
  cell: ({ row }) => {
    const diagnosisText = row.getValue("diagnosisText") as string | null;

    return (
      <CellWrapper>
        <CopyableContent text={diagnosisText ?? "N/A"} />
      </CellWrapper>
    );
  },
  enableColumnFilter: false,
};

const columns: ColumnDef<Diagnosis>[] = [diagnosisCodeColumn, diagnosisTextColumn];

export function DiagnosisListTable({ diagnoses, fallbackDiagnosis }: DiagnosisListTableProps) {
  const displayDiagnoses: Diagnosis[] =
    diagnoses.length > 0
      ? diagnoses
      : fallbackDiagnosis
        ? [
            {
              diagnosisCode: fallbackDiagnosis.diagnosisCode,
              diagnosisText: fallbackDiagnosis.diagnosisText,
              diagnosisCodeType: null,
            },
          ]
        : [];

  if (displayDiagnoses.length === 0) {
    return null;
  }

  const table = (
    <DataTable
      data={displayDiagnoses}
      columns={columns}
      ToolbarComponent={EmptyToolbar}
      getRowUrl={() => "#"}
      isLoading={false}
      showLoadingMessage={false}
      showPagination={false}
      emptyMessage="No diagnoses found."
    />
  );

  return <div className="overflow-x-auto border border-muted rounded-md">{table}</div>;
}
